/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

/**
 * Provides a hook which measures the live distance, in pixels, from the
 * viewport's top edge to the given ESRI `MapView`'s own top edge.
 *
 * This is used to position portal-based map widget panels relative to the
 * map's top-right corner rather than in-tree, since ancestor containers
 * (e.g. a resizable grid container) may clip a panel once they shrink the
 * map area. The map's top edge stays stable across such resizes - only its
 * bottom edge moves - so this offset only needs to be recalculated when the
 * map's own container actually changes size, or the window is resized.
 *
 * ```typescript
 *
 *   import {useMapTopOffset} from '@ion/features/src/maps/hooks/useMapTopOffset';
 *
 *   const mapTopOffset = useMapTopOffset(view);
 *
 * ```
 *
 * @param   view The ESRI `MapView` whose own container's top edge is
 *          measured.
 * @returns The live distance, in pixels, from the viewport's top edge to
 *          the map's top edge, or `null` until the first measurement
 *          completes.
 */
export const useMapTopOffset = (view: __esri.MapView): number | null => {
  const [topOffset, setTopOffset] = useState<number | null>(null);

  useEffect(() => {
    const updateTopOffset = function() {
      const rect = view?.container?.getBoundingClientRect?.();
      if (rect) {
        setTopOffset(Math.max(rect.top, 0));
      }
    };

    updateTopOffset();

    const target = view?.container;
    const observer = target ? new ResizeObserver(updateTopOffset) : null;
    observer?.observe(target);
    window.addEventListener('resize', updateTopOffset);

    return function() {
      observer?.disconnect();
      window.removeEventListener('resize', updateTopOffset);
    };
  }, [view]);

  return topOffset;
};

export default useMapTopOffset;
